import React from 'react';
import { ExternalLink } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Section, Eyebrow, FadeIn, PageHero } from '../components/ui-bits';
import Breadcrumb from '../components/Breadcrumb';

// Combined image: A3ARQ (left half) + Constructora Daniel Salinas (right half)
const COMBINED_LOGO = 'https://customer-assets.emergentagent.com/job_bringing-light/artifacts/vvqexdzr_Captura%20de%20pantalla%202026-05-13%20a%20la%28s%29%204.13.06%E2%80%AFp.m..png';

const LOGOS = {
  tolten: 'https://customer-assets.emergentagent.com/job_bringing-light/artifacts/y7ty61um_Logo%20Tolten.png',
};

// Renders one half of the combined A3ARQ + Salinas image (icon area on top)
const HalfLogo = ({ side, alt }) => (
  <div
    role="img"
    aria-label={alt}
    className="w-full h-32 rounded-2xl border border-white/10 mb-6"
    style={{
      background: '#6469a0',
      backgroundImage: `url(${COMBINED_LOGO})`,
      backgroundSize: '200% auto',
      backgroundPosition: side === 'left' ? '0% 22%' : '100% 22%',
      backgroundRepeat: 'no-repeat',
    }}
  />
);

const GestionInmobiliaria = () => {
  const { t, lang } = useLanguage();

  const steps = [
    { n: '01', title: t.v1Page.step1, body: t.v1Page.step1Body },
    { n: '02', title: t.v1Page.step2, body: t.v1Page.step2Body },
    { n: '03', title: t.v1Page.step3, body: t.v1Page.step3Body },
    { n: '04', title: t.v1Page.step4, body: t.v1Page.step4Body },
    { n: '05', title: t.v1Page.step5, body: t.v1Page.step5Body },
  ];

  const partners = [
    {
      name: t.v1Page.partner1Name,
      desc: t.v1Page.partner1Desc,
      url: null,
      logoNode: (
        <div className="w-full h-28 rounded-2xl bg-[#0a1322] border border-white/10 flex items-center justify-center mb-6 overflow-hidden">
          <img
            src={LOGOS.tolten}
            alt="Toltén Gestión Inmobiliaria"
            className="max-w-[80%] max-h-[80%] object-contain"
            loading="lazy"
          />
        </div>
      ),
    },
    {
      name: t.v1Page.partner2Name,
      desc: t.v1Page.partner2Desc,
      url: null,
      logoNode: <HalfLogo side="left" alt="A3ARQ" />,
    },
    {
      name: t.v1Page.partner3Name,
      desc: t.v1Page.partner3Desc,
      url: 'https://www.constructorasalinas.cl',
      logoNode: <HalfLogo side="right" alt="Constructora Daniel Salinas" />,
    },
  ];

  return (
    <div data-testid="vertical-1-page">
      <Breadcrumb items={[
        { to: '/que-hacemos', label: lang === 'en' ? 'What We Do' : 'Qué Hacemos' },
        { to: null, label: t.verticals.v1.title },
      ]} />
      <PageHero eyebrow={t.v1Page.eyebrow} title={t.v1Page.title} intro={t.v1Page.intro} />

      <Section className="pb-24" dataTestId="v1-image-block">
        <FadeIn>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[16/8]">
            <img
              src="https://delabase.cl/wp-content/uploads/2026/01/ubicacio%CC%81n-volcan.jpg"
              alt="Proyecto Delabase – Edificio Volcán, Villarrica"
              className="absolute inset-0 w-full h-full object-cover opacity-85"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070d18] via-[#070d18]/40 to-transparent" />
          </div>
        </FadeIn>
      </Section>

      <Section className="pb-24" dataTestId="v1-process">
        <FadeIn>
          <Eyebrow>{t.v1Page.processTitle}</Eyebrow>
        </FadeIn>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <FadeIn key={s.n} delay={i * 0.06}>
              <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-7 h-full card-hover">
                <div className="font-display text-amber-300 text-sm tracking-widest">{s.n}</div>
                <h4 className="mt-3 font-display font-bold text-lg text-white tracking-tight">{s.title}</h4>
                <p className="mt-3 font-body text-sm text-white/55 leading-relaxed">{s.body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section className="pb-32" dataTestId="v1-partners">
        <FadeIn>
          <Eyebrow>{t.v1Page.partnersTitle}</Eyebrow>
        </FadeIn>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {partners.map((p, i) => (
            <FadeIn key={i} delay={i * 0.08}>
              <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-8 h-full card-hover flex flex-col" data-testid={`v1-partner-${i + 1}`}>
                {p.logoNode}
                <h4 className="font-display font-bold text-xl text-white tracking-tight">{p.name}</h4>
                <p className="mt-3 font-body text-white/55 leading-relaxed flex-1">{p.desc}</p>
                {p.url && (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-1.5 text-sm text-amber-300 hover:text-amber-200 self-start"
                    data-testid={`v1-partner-link-${i + 1}`}
                  >
                    {t.common.visitSite} <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default GestionInmobiliaria;
